import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { initiativesApi, reportsApi, metricsApi } from '../services/trackerApi';
import { SubmitRollupReportPage } from './SubmitRollupReportPage';
import { useToast } from '../hooks/useToast';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { DateField } from '../components/DateField';
import { RichTextField, isRichTextEmpty } from '../components/RichTextField';
import { plainTextToHtml } from '../components/RichTextView';
import { milestoneCompletion } from '../utils/format';
import type { Initiative, Milestone, FunctionalRequirement, WeeklyReport } from '../types/trackerModels';
import './SubmitReportPage.css';

const weekOf = () => {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return monday.toISOString().slice(0, 10);
};
const todayIso = () => new Date().toISOString().slice(0, 10);

type MilestoneUpdate = { milestoneId: string; status: Milestone['status']; completionPercent: number; evidenceNote?: string };
type RequirementUpdate = { requirementId: string; status: string; evidenceNote?: string };
type Commitment = { kind: 'milestone' | 'requirement'; id: string; label: string };
type Health = 'On Track' | 'At Risk' | 'Blocked';

const defaultUpdateFor = (m: Milestone): MilestoneUpdate => ({
  milestoneId: m.id,
  status: m.status,
  completionPercent: typeof m.completionPercent === 'number' ? m.completionPercent : (m.status === 'Done' ? 100 : 0),
});

const HEALTH_VALUES: Health[] = ['On Track', 'At Risk', 'Blocked'];
const HEALTH_TONE: Record<Health, 'success' | 'warning' | 'danger'> = {
  'On Track': 'success',
  'At Risk': 'warning',
  Blocked: 'danger',
};
const HEALTH_ICON: Record<Health, string> = {
  'On Track': 'ri-checkbox-circle-line',
  'At Risk': 'ri-alert-line',
  Blocked: 'ri-close-circle-line',
};

const STEPS = [
  { icon: 'ri-calendar-line', label: 'This week' },
  { icon: 'ri-flag-line', label: 'Progress' },
  { icon: 'ri-list-check-2', label: 'Requirements' },
  { icon: 'ri-edit-2-line', label: 'Update' },
  { icon: 'ri-shield-check-line', label: 'Schedule' },
];

const STATUS_CLASS: Record<string, string> = {
  Done: 'st-done',
  Upcoming: 'st-upcoming',
  'At Risk': 'st-at-risk',
  Missed: 'st-missed',
};
// Same vocabulary as REQ_STATUS_OPTIONS on the Overview tab (InitiativeDetailPage.tsx) - this
// form has to offer exactly what that one does, or a status set here couldn't be re-selected there.
const REQUIREMENT_STATUSES = ['Proposed', 'Approved', 'In Progress', 'Done'];

const RING_R = 52;
const RING_CIRC = 2 * Math.PI * RING_R;

// Thin tier dispatcher - Program/Portfolio tier gets a fully separate component
// (SubmitRollupReportPage) with its own independent hook chain, sourced from this
// initiative's children rather than its own (empty) milestones/requirements. Two separate
// mounted components rather than an early-return inside one, so switching tiers never changes
// how many hooks a single component instance calls across renders (Rules of Hooks).
export const SubmitReportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: initiative } = useQuery<Initiative>({
    queryKey: ['initiative', id],
    queryFn: () => initiativesApi.getOne(id!),
    enabled: !!id,
  });
  if (!initiative) return <p className="text-muted container-xxl flex-grow-1 container-p-y">Loading…</p>;
  if (initiative.tier === 'Program' || initiative.tier === 'Portfolio') return <SubmitRollupReportPage initiative={initiative} />;
  return <SubmitProjectReportPage />;
};

const SubmitProjectReportPage: React.FC = () => {
  const { id, reportId } = useParams<{ id: string; reportId?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { personId } = useCurrentUser();

  const { data: initiative } = useQuery<Initiative>({
    queryKey: ['initiative', id],
    queryFn: () => initiativesApi.getOne(id!),
    enabled: !!id,
  });
  const { data: reports = [] } = useQuery<WeeklyReport[]>({
    queryKey: ['reports', id],
    queryFn: () => reportsApi.list(id!),
    enabled: !!id,
  });
  const { data: allMetrics = [] } = useQuery({ queryKey: ['metrics'], queryFn: metricsApi.getAll });
  const targetMetrics = allMetrics.filter((m: any) => initiative?.targetMetricIds?.includes(m.metricId));

  // Which week this report is filed under. Defaults to the current week, but is editable
  // (unless explicitly editing a specific past report via :reportId) — mainly so testing
  // functionality doesn't require waiting for real calendar weeks to pass.
  const [selectedWeek, setSelectedWeek] = useState(weekOf());

  // Explicit edit (via the Reports tab's Edit button) uses :reportId. Arriving fresh from
  // "Submit this week's report" auto-detects an existing report for the selected week and
  // edits that instead of creating a confusing duplicate for the same week.
  const existingReport = reportId
    ? reports.find((r) => r.reportId === reportId)
    : reports.find((r) => r.weekOf === selectedWeek);
  const isEditing = !!existingReport;

  // The most recent report strictly before the one being created/edited - the baseline every
  // delta (requirement changes, week-over-week %, "did you move what you committed to")
  // reads against. None for the very first report ever - deltas simply don't render then.
  const previousReport = reports
    .filter((r) => r.reportId !== existingReport?.reportId)
    .filter((r) => r.weekOf < (existingReport?.weekOf || selectedWeek))
    .sort((a, b) => b.weekOf.localeCompare(a.weekOf))[0];

  // Initiatives with weighted milestones report progress by updating milestone status —
  // percentComplete is derived from that (server-authoritative) rather than typed directly.
  // Initiatives with no weighted milestones fall back to the old manual number.
  const totalWeight = (initiative?.milestones || []).reduce((s, m) => s + (m.weight || 0), 0);
  const weightedMode = totalWeight > 0;

  const allRequirements: FunctionalRequirement[] = [
    ...(initiative?.functionalRequirements || []),
    ...(initiative?.nonFunctionalRequirements || []),
  ];

  const [percentComplete, setPercentComplete] = useState(0);
  const [milestoneUpdates, setMilestoneUpdates] = useState<MilestoneUpdate[]>([]);
  const [requirementUpdates, setRequirementUpdates] = useState<RequirementUpdate[]>([]);
  const [showAllRequirements, setShowAllRequirements] = useState(false);
  const [committedNext, setCommittedNext] = useState<Commitment[]>([]);
  const [metricReadingInputs, setMetricReadingInputs] = useState<Record<string, string>>({});
  const [health, setHealth] = useState<Health>('On Track');
  const [summary, setSummary] = useState('');
  const [blockers, setBlockers] = useState('');
  const [nextSteps, setNextSteps] = useState('');
  const [projectedEndDate, setProjectedEndDate] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');
  const [riskCreatedFromBlockers, setRiskCreatedFromBlockers] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRefs = useRef<(HTMLElement | null)[]>([null, null, null, null, null]);

  useEffect(() => {
    if (loaded) return;
    if (existingReport) {
      setSelectedWeek(existingReport.weekOf);
      setPercentComplete(existingReport.percentComplete);
      setHealth(existingReport.health as any);
      setSummary(existingReport.summary);
      setBlockers(existingReport.blockers);
      setNextSteps(existingReport.nextSteps);
      setProjectedEndDate(existingReport.projectedEndDate);
      const byId = new Map((existingReport.milestoneUpdates || []).map((u) => [u.milestoneId, u]));
      setMilestoneUpdates((initiative?.milestones || []).map((m) => byId.get(m.id) || defaultUpdateFor(m)));
      const reqById = new Map((existingReport.requirementUpdates || []).map((u) => [u.requirementId, u]));
      setRequirementUpdates(allRequirements.map((r) => reqById.get(r.id) || { requirementId: r.id, status: r.status }));
      // A Project's own committedNext only ever contains 'milestone'/'requirement' kinds - the
      // 'initiative' kind is Programme/Portfolio-only (SubmitRollupReportPage.tsx) - the field
      // is typed as the wider union on WeeklyReport since it's shared across tiers.
      setCommittedNext((existingReport.committedNext || []) as Commitment[]);
      setLoaded(true);
    } else if (initiative && reports) {
      const suggested = milestoneCompletion(initiative.milestones || []);
      setPercentComplete(suggested ?? initiative.percentComplete);
      setMilestoneUpdates((initiative.milestones || []).map(defaultUpdateFor));
      // Seed from the previous report's stored statuses, not the initiative's live status -
      // this IS the diff baseline. Falling back to current status for anything the previous
      // report never recorded means a brand-new requirement (or the very first report ever)
      // reads as "no change", never a fabricated one.
      const prevReqById = new Map((previousReport?.requirementUpdates || []).map((u) => [u.requirementId, u]));
      setRequirementUpdates(allRequirements.map((r) => prevReqById.get(r.id) || { requirementId: r.id, status: r.status }));
      setCommittedNext([]);
      setProjectedEndDate(initiative.currentProjectedEndDate);
      setHealth((initiative.health as any) === 'Not Started' ? 'On Track' : (initiative.health as any));
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingReport, initiative, reports, loaded]);

  // Scroll-spy for the rail: highlights whichever section is currently in view and fills
  // the connecting line up to it, so the rail always reflects where the user actually is.
  useEffect(() => {
    if (!loaded) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.indexOf(entry.target as HTMLElement);
            if (idx !== -1) setActiveStep(idx);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-10% 0px -55% 0px' }
    );
    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [loaded]);

  const updateMilestoneField = (milestoneId: string, patch: Partial<MilestoneUpdate>) => {
    setMilestoneUpdates((prev) => prev.map((u) => (u.milestoneId === milestoneId ? { ...u, ...patch } : u)));
  };
  const updateRequirementField = (requirementId: string, patch: Partial<RequirementUpdate>) => {
    setRequirementUpdates((prev) => prev.map((u) => (u.requirementId === requirementId ? { ...u, ...patch } : u)));
  };
  const toggleCommitment = (c: Commitment) => {
    setCommittedNext((prev) => (prev.some((x) => x.id === c.id) ? prev.filter((x) => x.id !== c.id) : [...prev, c]));
  };

  const computedPercent = weightedMode
    ? milestoneCompletion((initiative?.milestones || []).map((m) => {
        const u = milestoneUpdates.find((x) => x.milestoneId === m.id);
        return u ? { ...m, status: u.status, completionPercent: u.completionPercent } : m;
      })) ?? 0
    : null;
  const overallPercent = weightedMode ? (computedPercent ?? 0) : percentComplete;
  const ringOffset = RING_CIRC - (RING_CIRC * Math.min(100, Math.max(0, overallPercent))) / 100;
  const weekDelta = previousReport ? overallPercent - previousReport.percentComplete : null;

  // Requirement rows split into "changed since last report" (shown expanded) and "unchanged"
  // (collapsed into one line) - re-confirming everything that didn't move is exactly the
  // overhead this is meant to remove.
  const requirementRows = allRequirements.map((r) => {
    const previousStatus = previousReport?.requirementUpdates?.find((u) => u.requirementId === r.id)?.status ?? r.status;
    const current = requirementUpdates.find((u) => u.requirementId === r.id)?.status ?? r.status;
    return { requirement: r, previousStatus, current, changed: current !== previousStatus };
  });
  const changedRequirementRows = requirementRows.filter((r) => r.changed);
  const unchangedRequirementRows = requirementRows.filter((r) => !r.changed);

  const openCommitmentOptions: Commitment[] = [
    ...(initiative?.milestones || [])
      .filter((m) => m.status !== 'Done')
      .map((m) => ({ kind: 'milestone' as const, id: m.id, label: m.name })),
    ...allRequirements
      .filter((r) => r.status !== 'Done')
      .map((r) => ({ kind: 'requirement' as const, id: r.id, label: r.text })),
  ];
  const isCommitmentDone = (c: Commitment) =>
    c.kind === 'milestone'
      ? (initiative?.milestones || []).find((m) => m.id === c.id)?.status === 'Done'
      : allRequirements.find((r) => r.id === c.id)?.status === 'Done';

  const openRisks = (initiative?.risks || []).filter((r) => r.status === 'Open');

  // Changing the week (only possible when not editing a specific past report via :reportId)
  // re-triggers the load effect so the form picks up whichever report, if any, already
  // exists for that week — same auto-detect behavior as landing on the page fresh.
  const handleWeekChange = (newWeek: string) => {
    setSelectedWeek(newWeek);
    setLoaded(false);
  };

  const scrollToStep = (idx: number) => {
    sectionRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Once currentProjectedEndDate has been set once, changing it requires an exception —
  // held pending until an approver signs off, rather than taking effect immediately. Only
  // one date-change request can be in flight per initiative, so further changes are blocked
  // while one is already pending.
  const pendingBlocked = !!initiative?.pendingProjectedEndDate;
  const dateChanged = !!initiative?.currentProjectedEndDate && !pendingBlocked && projectedEndDate !== initiative.currentProjectedEndDate;

  const summaryEmpty = isRichTextEmpty(summary);
  const needsExceptionReason = dateChanged && !exceptionReason.trim();
  const canSubmit = !summaryEmpty && !needsExceptionReason;

  const metricReadingsPayload = () =>
    Object.entries(metricReadingInputs)
      .filter(([, v]) => v.trim() !== '')
      .map(([metricId, v]) => ({ metricId, value: Number(v) }));

  const submitMutation = useMutation({
    mutationFn: () => {
      const data: any = {
        health, summary, blockers, nextSteps, projectedEndDate,
        percentComplete: weightedMode ? computedPercent : percentComplete,
        milestoneUpdates: weightedMode ? milestoneUpdates : [],
        requirementUpdates,
        committedNext,
        metricReadings: metricReadingsPayload(),
      };
      if (dateChanged) data.exceptionReason = exceptionReason;
      return existingReport
        ? reportsApi.update(id!, existingReport.reportId, data)
        : reportsApi.create(id!, { ...data, weekOf: selectedWeek, submittedBy: personId });
    },
    onSuccess: () => {
      if (dateChanged) {
        toast.success('Report saved — the projected end date change is now pending approval.');
      } else {
        toast.success(existingReport ? 'Report updated.' : 'Report submitted.');
      }
      queryClient.invalidateQueries({ queryKey: ['reports', id] });
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      navigate(`/tracker/initiatives/${id}`);
    },
    onError: (e: any) => toast.error(e.message || 'Failed to save report'),
  });

  const createRiskFromBlockers = useMutation({
    mutationFn: () => {
      // Risk.description elsewhere in the app is plain text, so strip the rich-text markup
      // rather than leaking raw HTML tags into the Risks & Blockers tab.
      const plainText = blockers.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      return initiativesApi.update(id!, {
        risks: [...(initiative?.risks || []), { id: `RSK-${Date.now()}`, description: plainText, severity: 'Medium', status: 'Open' }],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setRiskCreatedFromBlockers(true);
      toast.success('Risk created from blockers text.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to create risk'),
  });

  // Bedrock draft-assist: sends exactly the deltas already computed/rendered above, gets back
  // plain-text prose, and fills the three fields below - never submits on its own, the
  // reporter reviews/edits everything before actually saving.
  const draftMutation = useMutation({
    mutationFn: () => {
      const milestoneChanges = (initiative?.milestones || [])
        .map((m) => {
          const u = milestoneUpdates.find((x) => x.milestoneId === m.id);
          return u && u.status !== m.status ? { name: m.name, from: m.status, to: u.status } : null;
        })
        .filter(Boolean);
      const requirementChanges = changedRequirementRows.map((r) => ({
        text: r.requirement.text, from: r.previousStatus, to: r.current,
      }));
      const lateMilestones = (initiative?.milestones || [])
        .filter((m) => m.targetDate && m.targetDate < todayIso() && m.status !== 'Done')
        .map((m) => m.name);
      const metricReadings = metricReadingsPayload().map((mr) => {
        const metric = targetMetrics.find((m: any) => m.metricId === mr.metricId);
        return {
          name: metric?.name || '', unit: metric?.unit || '', value: mr.value,
          onTarget: metric?.target == null ? true : (metric.direction === 'lower' ? mr.value <= metric.target : mr.value >= metric.target),
        };
      });
      return reportsApi.draft(id!, {
        milestoneChanges, requirementChanges, lateMilestones, metricReadings,
        previousNextSteps: previousReport ? previousReport.nextSteps.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '',
        health,
      });
    },
    onSuccess: (draft) => {
      setSummary(plainTextToHtml(draft.summary || ''));
      setBlockers(plainTextToHtml(draft.blockers || ''));
      setNextSteps(plainTextToHtml(draft.nextSteps || ''));
      toast.success('Draft generated — review and edit before submitting.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to generate a draft'),
  });

  if (!initiative || !loaded) return <p className="text-muted container-xxl flex-grow-1 container-p-y">Loading…</p>;

  return (
    <div className="container-xxl flex-grow-1 container-p-y rf">
      <div className="rf-container">
      <div className="rf-crumbs">
        <span>Initiative Tracker</span>
        <span className="sep">›</span>
        <span className="current">{initiative.name}</span>
      </div>

      <div className="rf-head">
        <div>
          <h1>{isEditing ? 'Edit Weekly Report' : 'Submit Weekly Report'}</h1>
          <p className="rf-sub">
            Reporting week <strong className="rf-tabular">{formatWeek(reportId ? (existingReport?.weekOf || selectedWeek) : selectedWeek)}</strong>
          </p>
        </div>
        <span className={`rf-chip rf-tone-${HEALTH_TONE[health]}`}>
          <i className={HEALTH_ICON[health]}></i>
          {health}
        </span>
      </div>

      {isEditing && (
        <div className="np-banner np-tone-info mb-3">
          <i className="ri-information-line"></i>
          <span>{reportId ? 'Editing a past report.' : "A report already exists for this week — editing it instead of creating a duplicate."}</span>
        </div>
      )}

      <div className="rf-layout">
        <aside className="rf-rail">
          <div className="rf-ring-wrap">
            <svg className="rf-ring" viewBox="0 0 120 120">
              <circle className="rf-ring-track" cx="60" cy="60" r={RING_R} />
              <circle className="rf-ring-fill" cx="60" cy="60" r={RING_R} strokeDasharray={RING_CIRC} strokeDashoffset={ringOffset} />
            </svg>
            <div className="rf-ring-label">
              <span className="rf-ring-pct rf-tabular">{overallPercent}%</span>
              <span className="rf-ring-caption">Overall</span>
            </div>
          </div>
          {weekDelta !== null && (
            <span className={`rf-delta-badge ${weekDelta >= 0 ? 'up' : 'down'}`}>
              <i className={weekDelta >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
              {Math.abs(weekDelta)}% vs last week
            </span>
          )}

          <nav className="rf-steps" style={{ ['--rf-fill' as any]: `${(activeStep / (STEPS.length - 1)) * 100}%` }}>
            {STEPS.map((step, i) => (
              <button
                key={step.label}
                type="button"
                className={`rf-step ${activeStep === i ? 'active' : ''}`}
                onClick={() => scrollToStep(i)}
              >
                <span className="rf-dot"><i className={step.icon}></i></span>
                <span>
                  <span className="rf-index">0{i + 1}</span>
                  <span className="rf-name">{step.label}</span>
                </span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="rf-content">
          <section id="s1" className="rf-block" ref={(el) => { sectionRefs.current[0] = el; }}>
            <div className="rf-block-head">
              <div className="rf-title-group">
                <span className="rf-icon-badge"><i className="ri-calendar-line"></i></span>
                <div>
                  <span className="rf-eyebrow">STEP 01</span>
                  <h2>This week</h2>
                </div>
              </div>
            </div>

            <div className="rf-row2">
              <div className="rf-field">
                <label className="rf-field-label">Reporting week</label>
                {reportId ? (
                  <div className="rf-date">
                    <input className="form-control" value={formatWeek(existingReport?.weekOf || selectedWeek)} readOnly />
                  </div>
                ) : (
                  <>
                    <div className="rf-date">
                      <DateField className="form-control" value={selectedWeek} onChange={handleWeekChange} />
                    </div>
                    <p className="rf-field-hint">Defaults to this week — change it to file/test a report against a different week.</p>
                  </>
                )}
              </div>

              <div className="rf-field">
                <label className="rf-field-label">Health this week</label>
                <div className="rf-health-tiles">
                  {HEALTH_VALUES.map((h) => (
                    <button
                      key={h}
                      type="button"
                      className={`rf-htile ${health === h ? 'active' : ''}`}
                      data-tone={HEALTH_TONE[h]}
                      onClick={() => setHealth(h)}
                    >
                      <span className="rf-hicon"><i className={HEALTH_ICON[h]}></i></span>
                      <span className="rf-hlabel">{h}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section id="s2" className="rf-block" ref={(el) => { sectionRefs.current[1] = el; }}>
            <div className="rf-block-head">
              <div className="rf-title-group">
                <span className="rf-icon-badge"><i className="ri-flag-line"></i></span>
                <div>
                  <span className="rf-eyebrow">STEP 02</span>
                  <h2>Progress</h2>
                </div>
              </div>
            </div>

            {weightedMode ? (
              (initiative.milestones || []).map((m) => {
                const u = milestoneUpdates.find((x) => x.milestoneId === m.id) || defaultUpdateFor(m);
                const isDone = u.status === 'Done';
                const isLate = !!m.targetDate && m.targetDate < todayIso() && !isDone;
                return (
                  <div key={m.id} className="rf-milestone">
                    <div className="rf-m-head">
                      <div className="rf-m-name">
                        {isDone && <span className="rf-check"><i className="ri-check-line"></i></span>}
                        <span className="rf-m-title">{m.name}</span>
                        {isLate && <span className="rf-chip rf-tone-danger rf-m-late">Target date passed</span>}
                      </div>
                      <div className="rf-m-tags">
                        {!!m.weight && <span className="rf-weight-tag">{m.weight}% WEIGHT</span>}
                        <select
                          className={`rf-status-select ${STATUS_CLASS[u.status] || 'st-upcoming'}`}
                          value={u.status}
                          onChange={(e) => updateMilestoneField(m.id, {
                            status: e.target.value as Milestone['status'],
                            completionPercent: e.target.value === 'Done' ? 100 : u.completionPercent,
                          })}
                        >
                          {['Upcoming', 'Done', 'At Risk', 'Missed'].map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="rf-m-track">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        className="rf-slider"
                        value={u.completionPercent}
                        disabled={isDone}
                        style={{ ['--rf-pct' as any]: `${u.completionPercent}%` }}
                        onChange={(e) => updateMilestoneField(m.id, { completionPercent: Number(e.target.value) })}
                      />
                      <span className="rf-m-pct rf-tabular">{u.completionPercent}%</span>
                    </div>
                    {isDone && (
                      <input
                        type="text"
                        className="rf-evidence-input"
                        placeholder="Optional: how was this confirmed? (link, note, etc.)"
                        value={u.evidenceNote || ''}
                        onChange={(e) => updateMilestoneField(m.id, { evidenceNote: e.target.value })}
                      />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="rf-field">
                <div className="rf-m-track">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    className="rf-slider"
                    value={percentComplete}
                    style={{ ['--rf-pct' as any]: `${percentComplete}%` }}
                    onChange={(e) => setPercentComplete(Number(e.target.value))}
                  />
                  <span className="rf-m-pct rf-tabular">{percentComplete}%</span>
                </div>
                <p className="rf-field-hint">Manual tracking — no milestones weighted on this initiative yet.</p>
              </div>
            )}

            {targetMetrics.length > 0 && (
              <div className="rf-field" style={{ marginTop: 24 }}>
                <label className="rf-field-label">This week's metrics</label>
                {targetMetrics.map((m: any) => (
                  <div key={m.metricId} className="rf-metric-row">
                    <span className="rf-metric-name">{m.name}</span>
                    <input
                      className="form-control"
                      style={{ maxWidth: 140 }}
                      placeholder={m.unit || 'Value'}
                      value={metricReadingInputs[m.metricId] || ''}
                      onChange={(e) => setMetricReadingInputs((prev) => ({ ...prev, [m.metricId]: e.target.value }))}
                    />
                    {m.unit && <span className="rf-metric-unit">{m.unit}</span>}
                  </div>
                ))}
                <p className="rf-field-hint">Only readings you enter here are logged — leave any blank to skip it this week.</p>
              </div>
            )}
          </section>

          <section id="s3" className="rf-block" ref={(el) => { sectionRefs.current[2] = el; }}>
            <div className="rf-block-head">
              <div className="rf-title-group">
                <span className="rf-icon-badge"><i className="ri-list-check-2"></i></span>
                <div>
                  <span className="rf-eyebrow">STEP 03</span>
                  <h2>Requirements</h2>
                </div>
              </div>
            </div>

            {requirementRows.length === 0 && (
              <p className="rf-field-hint">No functional or non-functional requirements recorded on this initiative.</p>
            )}
            {changedRequirementRows.length === 0 && requirementRows.length > 0 && (
              <p className="rf-field-hint">Nothing has changed since last report — all {requirementRows.length} unchanged below.</p>
            )}
            {changedRequirementRows.map(({ requirement: r }) => {
              const ru = requirementUpdates.find((u) => u.requirementId === r.id);
              const reqIsDone = (ru?.status ?? r.status) === 'Done';
              return (
                <div key={r.id} className="rf-requirement-group">
                  <div className="rf-requirement">
                    <span className="rf-req-text">{r.text}</span>
                    <select
                      className="rf-status-select"
                      value={ru?.status ?? r.status}
                      onChange={(e) => updateRequirementField(r.id, { status: e.target.value })}
                    >
                      {REQUIREMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  {reqIsDone && (
                    <input
                      type="text"
                      className="rf-evidence-input"
                      placeholder="Optional: how was this confirmed? (link, note, etc.)"
                      value={ru?.evidenceNote || ''}
                      onChange={(e) => updateRequirementField(r.id, { evidenceNote: e.target.value })}
                    />
                  )}
                </div>
              );
            })}

            {unchangedRequirementRows.length > 0 && (
              <div className="rf-unchanged-toggle">
                <button type="button" className="np-btn np-btn-sm np-btn-ghost" onClick={() => setShowAllRequirements((v) => !v)}>
                  <i className={`ri-arrow-${showAllRequirements ? 'up' : 'down'}-s-line`}></i>
                  {unchangedRequirementRows.length} unchanged since last report
                </button>
                {showAllRequirements && unchangedRequirementRows.map(({ requirement: r }) => {
                  const ru = requirementUpdates.find((u) => u.requirementId === r.id);
                  const reqIsDone = (ru?.status ?? r.status) === 'Done';
                  return (
                    <div key={r.id} className="rf-requirement-group">
                      <div className="rf-requirement">
                        <span className="rf-req-text">{r.text}</span>
                        <select
                          className="rf-status-select"
                          value={ru?.status ?? r.status}
                          onChange={(e) => updateRequirementField(r.id, { status: e.target.value })}
                        >
                          {REQUIREMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                      {reqIsDone && (
                        <input
                          type="text"
                          className="rf-evidence-input"
                          placeholder="Optional: how was this confirmed? (link, note, etc.)"
                          value={ru?.evidenceNote || ''}
                          onChange={(e) => updateRequirementField(r.id, { evidenceNote: e.target.value })}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section id="s4" className="rf-block" ref={(el) => { sectionRefs.current[3] = el; }}>
            <div className="rf-block-head">
              <div className="rf-title-group">
                <span className="rf-icon-badge"><i className="ri-edit-2-line"></i></span>
                <div>
                  <span className="rf-eyebrow">STEP 04</span>
                  <h2>Update</h2>
                </div>
              </div>
              <button
                type="button"
                className="np-btn np-btn-sm np-btn-ghost"
                disabled={draftMutation.isPending}
                onClick={() => draftMutation.mutate()}
              >
                {draftMutation.isPending ? <span className="spinner-border spinner-border-sm"></span> : <i className="ri-magic-line"></i>}
                Generate draft
              </button>
            </div>

            <div className="rf-field">
              <label className="rf-field-label">Summary</label>
              <RichTextField value={summary} onChange={setSummary} placeholder="What happened this week…" height={120} />
            </div>

            <div className="rf-field">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label className="rf-field-label" style={{ marginBottom: 0 }}>Blockers</label>
                {!isRichTextEmpty(blockers) && !riskCreatedFromBlockers && (
                  <button
                    type="button"
                    className="rf-risk-chip"
                    disabled={createRiskFromBlockers.isPending}
                    onClick={() => createRiskFromBlockers.mutate()}
                  >
                    <i className="ri-shield-flash-line"></i>Turn into tracked risk
                  </button>
                )}
                {riskCreatedFromBlockers && (
                  <span className="rf-risk-done"><i className="ri-check-line"></i>Risk created</span>
                )}
              </div>
              {openRisks.length > 0 && (
                <div className="rf-open-risks">
                  <span className="rf-field-hint" style={{ margin: '0 8px 0 0' }}>Still blocking?</span>
                  {openRisks.map((r) => (
                    <span key={r.id} className="rf-chip rf-tone-warning" title={r.description}>{r.description}</span>
                  ))}
                </div>
              )}
              <RichTextField
                value={blockers}
                onChange={(html) => { setBlockers(html); setRiskCreatedFromBlockers(false); }}
                placeholder="Anything blocking progress…"
                height={90}
              />
            </div>

            <div className="rf-field">
              <label className="rf-field-label">Next steps</label>
              <RichTextField value={nextSteps} onChange={setNextSteps} placeholder="Plans for next week…" height={90} />
            </div>

            <div className="rf-field">
              <label className="rf-field-label">Committing to next week</label>
              {!!previousReport?.committedNext?.length && (
                <div className="rf-commit-check-list">
                  <p className="rf-field-hint" style={{ marginTop: 0 }}>You committed to these last week:</p>
                  {previousReport.committedNext.map((c) => {
                    const done = isCommitmentDone(c as Commitment);
                    return (
                      <span key={c.id} className={`rf-chip ${done ? 'rf-tone-success' : 'rf-tone-warning'}`}>
                        <i className={done ? 'ri-check-line' : 'ri-time-line'}></i>{c.label}
                      </span>
                    );
                  })}
                </div>
              )}
              <p className="rf-field-hint">Tag 1–3 things you plan to move next week:</p>
              <div className="rf-commit-picker">
                {openCommitmentOptions.length === 0 && <span className="rf-field-hint">Nothing open to commit to.</span>}
                {openCommitmentOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`rf-chip rf-chip-toggle ${committedNext.some((c) => c.id === opt.id) ? 'active' : ''}`}
                    onClick={() => toggleCommitment(opt)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section id="s5" className="rf-block" ref={(el) => { sectionRefs.current[4] = el; }}>
            <div className="rf-block-head">
              <div className="rf-title-group">
                <span className="rf-icon-badge"><i className="ri-shield-check-line"></i></span>
                <div>
                  <span className="rf-eyebrow">STEP 05</span>
                  <h2>Schedule</h2>
                </div>
              </div>
            </div>

            <div className="rf-field">
              <div className="rf-schedule-row">
                <div>
                  <label className="rf-field-label">Updated projected end date</label>
                  {pendingBlocked ? (
                    <div className="rf-date">
                      <input className="form-control" value={initiative.currentProjectedEndDate} readOnly disabled />
                    </div>
                  ) : (
                    <div className="rf-date">
                      <DateField className="form-control" value={projectedEndDate} onChange={setProjectedEndDate} />
                    </div>
                  )}
                </div>
              </div>

              {pendingBlocked && (
                <div className="rf-pending-banner">
                  <i className="ri-time-line"></i>
                  A change to {initiative.pendingProjectedEndDate} is already pending approval — resolve that exception before requesting another change.
                </div>
              )}

              {dateChanged && (
                <div className="rf-exception-panel">
                  <div className="rf-ep-head">
                    <i className="ri-alert-line"></i>
                    Reason for schedule change — required
                  </div>
                  <p>Changing the projected end date after it's been set requires an exception. It won't take effect until an approver signs off, on the initiative's Exceptions tab.</p>
                  <textarea
                    rows={2}
                    placeholder="Why is the projected end date changing?"
                    value={exceptionReason}
                    onChange={(e) => setExceptionReason(e.target.value)}
                  />
                </div>
              )}

              {!pendingBlocked && !dateChanged && (
                <p className="rf-field-hint">Unchanged this week — no sign-off required.</p>
              )}
            </div>
          </section>
        </main>
      </div>
      </div>

      <footer className="rf-action-bar">
        <div className="rf-action-inner">
          <span className={`rf-status-msg ${canSubmit ? 'ready' : 'blocked'}`}>
            <i className={canSubmit ? 'ri-checkbox-circle-line' : 'ri-information-line'}></i>
            {summaryEmpty ? 'Summary required' : needsExceptionReason ? 'Reason for schedule change required' : 'Ready to submit'}
          </span>
          <div className="rf-actions">
            <button className="np-btn np-btn-ghost" onClick={() => navigate(`/tracker/initiatives/${id}`)}>Cancel</button>
            <button
              className="np-btn np-btn-primary"
              disabled={submitMutation.isPending || !canSubmit}
              onClick={() => submitMutation.mutate()}
            >
              {submitMutation.isPending ? (
                <span className="spinner-border spinner-border-sm"></span>
              ) : (
                <i className={isEditing ? 'ri-save-line' : 'ri-send-plane-line'}></i>
              )}
              {isEditing ? 'Save changes' : 'Submit report'}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

function formatWeek(iso: string) {
  const start = new Date(iso);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${start.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} – ${end.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}
